import { RangeValue } from '@react-types/shared';
import { useCallback } from 'react';
import { DateValue } from 'react-aria';
import { connect, ConnectedProps } from 'react-redux';
import { useParams } from 'react-router-dom-v5-compat';

import { MessageType } from 'dashboard/lib/interfaces/Message';
import { WorkflowRunStatus } from 'dashboard/models/workflow-run';
import { showMessage } from 'dashboard/redux/actions/messages';
import {
  setCurrentPage as setRunsCurrentPage,
  setPageSize,
  setWorkflowRunFilters,
  setWorkflowRunSortParams,
  WorkflowRunsFilterParams,
} from 'dashboard/redux/actions/workflow-runs';
import {
  clearWorkflowVersions,
  setCurrentPage as setVersionsCurrentPage,
} from 'dashboard/redux/actions/workflow-versions';
import { RootState } from 'dashboard/redux/store';
import { loadWorkflowRunList } from 'dashboard/redux/thunk-actions/workflow-runs';
import { loadWorkflowVersionList } from 'dashboard/redux/thunk-actions/workflow-versions';
import DashboardCaseActionRunsView from 'dashboard/views/DashboardCaseActionRunsView';
import useApiLoader from 'lib/hooks/useApiLoader';
import useOnMountAndUnmount from 'lib/hooks/useOnMountAndUnmount';
import useTable from 'lib/hooks/useTable';
import { ApiModel } from 'lib/interfaces/api';
import { isPermissionsError, permissionsErrorMessage } from 'lib/permissions-errors';
import { translateDateValuetoLuxon } from 'lib/utils/filters';

type DashboardCaseActionRunsRouteProps = PropsFromRedux;

type Params = { actionWorkflowId: string };

const DashboardCaseActionRunsRoute = (props: DashboardCaseActionRunsRouteProps) => {
  const {
    currentEnvironment,
    runsCurrentPage,
    versionsCurrentPage,
    versionsTotalPages,
    workflowRunFilters,
    workflowRunSortParams,
    pageSize,
  } = props;

  const { actionWorkflowId } = useParams<Params>() as Params; // actionWorkflowId will always be defined

  useOnMountAndUnmount(props.clearWorkflowVersions);

  const { handlePageLinkClick, handlePageSizeClick, handleSortUpdate, handleDateUpdate } = useTable(
    {
      filters: workflowRunFilters,
      sortParams: workflowRunSortParams,
      pageSize: pageSize,
      currentPage: runsCurrentPage,
      setFilters: props.setWorkflowRunFilters,
      setSortParams: props.setWorkflowRunSortParams,
      setCurrentPage: props.setRunsCurrentPage,
      setPageSize: props.setPageSize,
    },
  );

  const { loading: isLoadingWorkflowRuns } = useApiLoader({
    loader: () =>
      props.loadWorkflowRunList({
        currentEnvironment,
        currentPage: runsCurrentPage,
        pageSize,
        filters: {
          ...workflowRunFilters,
          workflowIds: [actionWorkflowId],
          triggerType: ApiModel.TriggerCase,
        } as WorkflowRunsFilterParams,
        sortParams: workflowRunSortParams,
      }),
    deps: [
      props.loadWorkflowRunList,
      currentEnvironment,
      runsCurrentPage,
      pageSize,
      workflowRunFilters,
      workflowRunSortParams,
      actionWorkflowId,
    ],
    onError: (error) => {
      if (isPermissionsError(error)) {
        props.showMessage(permissionsErrorMessage(error), MessageType.Failure);
      } else {
        props.showMessage('Failed to load case action runs.', MessageType.Failure);
      }
    },
    onNetworkError: () =>
      props.showMessage(
        'We are experiencing network issues. Please try again.',
        MessageType.Failure,
      ),
  });

  const { loading: isLoadingWorkflowVersions } = useApiLoader({
    loader: () =>
      props.loadWorkflowVersionList({
        workflowId: actionWorkflowId,
        triggerType: ApiModel.TriggerCase,
        currentPage: versionsCurrentPage,
        pageSize,
      }),
    deps: [
      props.loadWorkflowVersionList,
      currentEnvironment,
      versionsCurrentPage,
      pageSize,
      actionWorkflowId,
    ],
    onError: (error) => {
      if (isPermissionsError(error)) {
        props.showMessage(permissionsErrorMessage(error), MessageType.Failure);
      } else {
        props.showMessage('Failed to load case action versions.', MessageType.Failure);
      }
    },
    onNetworkError: () =>
      props.showMessage(
        'We are experiencing network issues. Please try again.',
        MessageType.Failure,
      ),
  });

  const handleDateUpdateV2 = useCallback(
    (newDateRange: RangeValue<DateValue> | null) => {
      if (newDateRange) {
        const { startDate, endDate } = translateDateValuetoLuxon(newDateRange);
        if (startDate || endDate) handleDateUpdate({ startDate, endDate });
      }
    },
    [handleDateUpdate],
  );

  const handleStatusFilterUpdate = useCallback(
    (status: WorkflowRunStatus[]) => {
      props.setWorkflowRunFilters({ status });
      props.setRunsCurrentPage(1);
    },
    [props.setWorkflowRunFilters, props.setRunsCurrentPage],
  );

  const handleVersionFilterUpdate = useCallback(
    (workflowVersionIds: string[]) => {
      props.setWorkflowRunFilters({ workflowVersionIds });
      props.setRunsCurrentPage(1);
    },
    [props.setRunsCurrentPage, props.setWorkflowRunFilters],
  );

  const handleOmniFilterUpdate = useCallback(
    (omni: string) => {
      props.setWorkflowRunFilters({ omni });
      props.setRunsCurrentPage(1);
    },
    [props.setRunsCurrentPage, props.setWorkflowRunFilters],
  );

  const handleWorkflowIdFilterUpdate = useCallback(
    (workflowIds: string[]) => {
      props.setWorkflowRunFilters({ workflowIds });
      props.setRunsCurrentPage(1);
    },
    [props.setRunsCurrentPage, props.setWorkflowRunFilters],
  );

  const onLoadMoreVersions =
    versionsCurrentPage < versionsTotalPages
      ? () => props.setVersionsCurrentPage(versionsCurrentPage + 1)
      : undefined;

  return (
    <DashboardCaseActionRunsView
      workflowRuns={props.workflowRuns}
      workflowRunFilters={workflowRunFilters}
      workflowId={actionWorkflowId}
      workflowRunPaginationParams={props.workflowRunPaginationParams}
      pageSize={pageSize}
      workflowVersions={props.workflowVersions}
      workflowsMap={props.workflowsMap}
      isLoadingWorkflowVersions={isLoadingWorkflowVersions}
      isLoadingWorkflowRuns={isLoadingWorkflowRuns}
      sortParams={props.workflowRunSortParams}
      handlePageLinkClick={handlePageLinkClick}
      handlePageSizeClick={handlePageSizeClick}
      handleDateUpdate={handleDateUpdateV2}
      handleStatusFilterUpdate={handleStatusFilterUpdate}
      handleVersionFilterUpdate={handleVersionFilterUpdate}
      handleSortUpdate={handleSortUpdate}
      onLoadMoreVersions={onLoadMoreVersions}
      handleWorkflowIdFilterUpdate={handleWorkflowIdFilterUpdate}
      handleOmniFilterUpdate={handleOmniFilterUpdate}
    />
  );
};

const mapStateToProps = (state: RootState) => {
  return {
    workflowRuns:
      state.workflowRuns.workflowRuns[state.currentEnvironment.currentEnvironment.id] || [],
    workflowRunFilters: state.workflowRuns.filters,
    workflowRunSortParams: state.workflowRuns.sortParams,
    listsSortParams: state.lists.sortParams,
    workflowRunPaginationParams: state.workflowRuns.paginationParams,
    workflowsMap: state.workflowRuns.workflows,
    currentEnvironment: state.currentEnvironment.currentEnvironment,
    runsCurrentPage: state.workflowRuns.currentPage,
    pageSize: state.workflowRuns.pageSize,
    workflowVersions: state.workflowVersions.workflowVersions,
    versionsCurrentPage: state.workflowVersions.currentPage,
    versionsTotalPages: state.workflowVersions.paginationParams?.['total-pages'],
  };
};

const mapDispatchToProps = {
  loadWorkflowRunList,
  loadWorkflowVersionList,
  clearWorkflowVersions,
  setWorkflowRunFilters,
  setWorkflowRunSortParams,
  setPageSize,
  setRunsCurrentPage,
  setVersionsCurrentPage,
  showMessage,
};

const connector = connect(mapStateToProps, mapDispatchToProps);
type PropsFromRedux = ConnectedProps<typeof connector>;

export default connector(DashboardCaseActionRunsRoute);
